import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import {
  OptLogVolumeActions,
  OptLogVolumeActionsDetailsResponse,
} from '~/shared/types/api';
import {
  mergeActionActorDetails,
  needsActionActorDetailsFetch,
  toOptLogActionsDetailsUrl,
} from '~/shared/utils/actionActor';
import { useXFetch } from '~/shared/utils/useXFetch';

export interface UseOptLogActionDetailsParams {
  tableQueryUrl: string;
  actions: OptLogVolumeActions[];
  canReadUsers: boolean;
  enabled?: boolean;
}

export function useOptLogActionDetails({
  tableQueryUrl,
  actions,
  canReadUsers,
  enabled = true,
}: UseOptLogActionDetailsParams): OptLogVolumeActions[] {
  const api = useXFetch();
  const shouldFetch =
    enabled && canReadUsers && !!tableQueryUrl && needsActionActorDetailsFetch(actions);

  const { data: details } = useQuery({
    queryKey: ['optLog', 'action-details', tableQueryUrl],
    enabled: shouldFetch,
    refetchOnWindowFocus: false,
    refetchInterval: shouldFetch ? TABLE_LIVE_REFRESH_MS : false,
    retry: false,
    queryFn: async () => {
      const response = await api<OptLogVolumeActionsDetailsResponse>(
        toOptLogActionsDetailsUrl(tableQueryUrl)
      );
      return response.actions ?? [];
    },
  });

  return useMemo(() => {
    if (!shouldFetch || !details?.length) {
      return actions;
    }

    return mergeActionActorDetails(actions, details);
  }, [actions, details, shouldFetch]);
}
